import clsx from 'clsx';
import React, { ReactNode, useMemo } from 'react';
import { ArrowRight, Calendar, Package } from 'lucide-react';
import { IonAvatar } from '@ionic/react';
import orderStatusThemeStyle from './orderStatusThemeStyle';
import { useDialog } from '../../../context/Dialog';
import OrderDetailsDialogue from './OrderDetailsDialogue';
import { images } from 'ionic-ui';
import { transformToAge } from '../../../utils/helperFunction/transformDate';
import OrderStatusChip from './OrderStatusChip';
import { OrderStatusType, orderType } from '../../../types/order/orderType';
import { formatPrice } from '../../../utils/formatPrice';
import useOrderActions from '../../../services/orders/hooks/useOrderActions';

type Props = orderType;

//BUTTON FOR DISPLAYING THE REST OF ORDER DETAILS
const ViewOrderButton = ({
  status,
  details,
}: {
  status: OrderStatusType;
  details: Props;
}) => {
  const themeStyle = orderStatusThemeStyle(status);
  const { openDialog } = useDialog();

  const handleClick = () => openDialog(<OrderDetailsDialogue {...details} />);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`cursor-pointer w-full p-1 flex hover:opacity-80 opacity-100 items-center rounded-lg ${themeStyle}`}
    >
      <small
        className={`bg-white text-sm text-nowrap rounded-lg px-2 py-0.5 border border-inherit`}
      >
        View order
      </small>
      <small className="text-left truncate flex-1 flex items-center font-medium gap-4 px-2">
        <span className="flex-1 truncate">
          <span>Order {details.reference} </span>
          <span className="max-sm:hidden">
            {status === 'cancelled' && 'cancelled'}
            {status === 'delivered' && 'has been delivered'}
            {status === 'pending' && 'is being processed'}
            {status === 'shipped' && 'has been shipped'}
          </span>
        </span>
        <span className="max-md:hidden">
          <ArrowRight className="size-4 ml-1" />
        </span>
      </small>
    </button>
  );
};

//UI THAT CONTAINS BASIC INFORMATION OF ORDER
const OrderDetails = ({
  status,
  created_at,
  total_price,
  quantity,
  unit_price,
  default_image_url,
  customer,
  product_name,
}: Pick<
  orderType,
  | 'default_image_url'
  | 'customer'
  | 'status'
  | 'total_price'
  | 'created_at'
  | 'quantity'
  | 'unit_price'
  | 'product_name'
>) => {
  return (
    <div className="flex flex-wrap w-full max-w-full py-2 gap-4">
      <div className="flex flex-1 justify-center flex-wrap gap-4">
        <IonAvatar className="h-14 w-14 sm:w-16 sm:h-16 ">
          <img
            src={default_image_url || images.placeholder}
            className="h-14 w-14 sm:w-16 sm:h-16 object-cover object-center"
          />
        </IonAvatar>
        <div className="space-y-2 flex-1">
          <div className="flex items-center flex-wrap gap-2">
            <h6
              className="font-medium high-emphasis-text flex-1 line-clamp-2 md:min-w-48 "
              title={product_name || 'product name'}
            >
              {product_name || 'product name'}
            </h6>
            <p className="text-primary w-full md:w-fit font-semibold">
              {formatPrice(total_price)}
            </p>
          </div>
          <div className=" capitalize flex items-center gap-2 flex-wrap">
            <small className="flex items-center gap-1 medium-emphasis-text">
              <span>quantity:</span>
              <span className="font-semibold">x{quantity}</span>
            </small>
            <small className="flex items-center gap-1 medium-emphasis-text">
              <span>unit price:</span>
              <span className="font-semibold">{formatPrice(unit_price)}</span>
            </small>
          </div>
          <p
            className="capitalize medium-emphasis-text line-clamp-2 font-medium"
            title={customer?.full_name || 'customer name'}
          >
            {customer?.full_name || 'customer name'}
          </p>
          <small className=" flex items-center gap-2 py-0.5 medium-emphasis-text">
            <span>
              <Calendar className="size-4" />
            </span>
            <span className="font-medium">
              {transformToAge(created_at, { maxDayOld: 1 })}
            </span>
          </small>
          <OrderStatusChip status={status} />
        </div>
      </div>
    </div>
  );
};

function OrderActionButtons({
  reference,
  status,
  product_name,
}: Pick<Props, 'reference' | 'status' | 'product_name'>) {
  const { markAsPacked, markAsShipped, markAsDelivered } = useOrderActions();

  const ACTIONS_MAP: Partial<
    Record<
      OrderStatusType,
      {
        label: string;
        onClick: () => void | Promise<void>;
        icon: ReactNode;
        destructive?: boolean;
      }[]
    >
  > = useMemo(
    () => ({
      successful: [
        {
          label: 'Mark as Packed',
          onClick: () => markAsPacked(reference, product_name),
          icon: <Package className="size-4" />,
          destructive: false,
        },
      ],
      marked_as_packed: [
        {
          label: 'Mark as Shipped',
          onClick: () => markAsShipped(reference, product_name),
          icon: <ArrowRight className="size-4" />,
          destructive: false,
        },
      ],
      shipped: [
        {
          label: 'Mark as Delivered',
          onClick: () => markAsDelivered(reference, product_name),
          icon: <ArrowRight className="size-4" />,
          destructive: false,
        },
      ],
    }),
    [reference, status]
  );
  const actions = ACTIONS_MAP[status] || [];

  if (actions.length < 1) return null;
  return (
    <>
      <span className="w-full h-[1px] bg-border block"></span>
      <div className="flex items-center flex-wrap gap-4">
        {actions.map((action, idx) => (
          <button
            key={idx}
            type="button"
            onClick={action.onClick}
            className={clsx(
              'flex-1 flex items-center gap-2 justify-center px-4 py-2 rounded-md border-2',
              action.destructive
                ? 'border-transparent hover:border-destructive-foreground hover:bg-destructive-foreground text-destructive'
                : 'hover:bg-primary hover:border-primary hover:text-white text-primary bg-customs-light-blue-foreground border-border'
            )}
          >
            {action.icon}
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </>
  );
}

export default function OrdersCard(props: Props) {
  return (
    <div className="p-2 sm:p-4 space-y-4 rounded-lg border border-border max-w-full">
      <ViewOrderButton details={props} status={props.status} />

      {/* ORDER DETAILS */}
      <OrderDetails {...props} />
      <OrderActionButtons
        reference={props.reference}
        status={props.status}
        product_name={props.product_name}
      />
    </div>
  );
}

// SKELETON
export function OrdersCardSkeleton() {
  return (
    <div className="p-4 space-y-4 rounded-lg border border-border bg-gray-50 animate-pulse">
      <span className="block rounded-md h-5 bg-gray-100" />

      {/* ORDER DETAILS */}
      <div className="flex w-full gap-4">
        <span className="block h-14 w-14 sm:w-16 sm:h-16 rounded-full bg-gray-100" />
        <div className="space-y-4 flex-1">
          <span className="block h-8 bg-gray-100" />
          <span className="block h-6 w-1/3 bg-gray-100" />
          <span className="block h-4 w-1/3 bg-gray-100" />
          <span className="block rounded-3xl h-10 w-1/3 bg-gray-100" />
        </div>
      </div>
      <div className="flex w-full gap-4">
        <span className="flex-1 block h-9 rounded-md bg-gray-100" />
        <span className="flex-1 block h-9 rounded-md bg-gray-100" />
      </div>
    </div>
  );
}
